import React, { useState, useEffect, useRef, useCallback } from 'react'
import InterviewSession from '../components/InterviewSession'
import DeviceTestScreen from '../components/DeviceTestScreen'
import SequentialInterviewSession from '../components/SequentialInterviewSession'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { ChatMessage, RecordingData } from '@bosmart/types'
import { InterviewMode } from '@bosmart/types'
import { setSentryContext } from '../sentry.client.config'
import {
  ApiRequestError,
  fetchInterviewAccessDetails,
  fetchInterviewQuestions,
  InterviewQuestion,
  resendInterviewOtp,
  verifyInterviewOtp,
} from '../lib/api-client'

// App flow states
type AppFlowState =
  | 'validating'
  | 'error'
  | 'otp'
  | 'consent'
  | 'device_test'
  | 'interview'
  | 'completed'

interface DeviceConfig {
  videoId: string
  audioId: string
}

interface TokenValidationState {
  isValidating: boolean
  isValid: boolean
  error: string | null
  interviewInfo: {
    candidateName: string
    jobTitle: string | null
    duration: number | null
  } | null
  interviewMode: InterviewMode
  requiresScreenSharing: boolean
}

export default function Home() {
  // Extract interviewId and token from URL
  const [publicInterviewId, setPublicInterviewId] = useState<string | null>(null)
  const [interviewId, setInterviewId] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [tokenState, setTokenState] = useState<TokenValidationState>({
    isValidating: true,
    isValid: false,
    error: null,
    interviewInfo: null,
    interviewMode: 'ai',
    requiresScreenSharing: false,
  })
  const [jobTitle, setJobTitle] = useState<string | null>(null)

  // App flow state
  const [flowState, setFlowState] = useState<AppFlowState>('validating')
  const [deviceConfig, setDeviceConfig] = useState<DeviceConfig | null>(null)
  const [questions, setQuestions] = useState<InterviewQuestion[]>([])
  const [otpCode, setOtpCode] = useState('')
  const [otpError, setOtpError] = useState<string | null>(null)
  const [otpInfoMessage, setOtpInfoMessage] = useState<string | null>(null)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [isResendingOtp, setIsResendingOtp] = useState(false)
  const [isOtpVerifiedTransitioning, setIsOtpVerifiedTransitioning] = useState(false)
  const [resendAvailableAt, setResendAvailableAt] = useState<number>(0)
  const [resendCountdown, setResendCountdown] = useState(0)
  const [otpProofToken, setOtpProofToken] = useState<string | null>(null)
  const otpTransitionTimeoutRef = useRef<number | null>(null)

  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [agreedToRecording, setAgreedToRecording] = useState(false)

  // Validate invitation token on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const id = window.location.pathname.split('/').filter(Boolean)[0] || params.get('id')
    const urlToken = params.get('token')

    if (!id) {
      setTokenState({
        isValidating: false,
        isValid: false,
        error: 'Interview ID is required',
        interviewInfo: null,
        interviewMode: 'ai', // Reset to default
        requiresScreenSharing: false, // Reset to default
      })
      setFlowState('error')
      return
    }

    setPublicInterviewId(id)

    if (urlToken) {
      setToken(urlToken)
    }

    // Validate token with API
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002'
    const apiUrl = urlToken
      ? `${API_BASE_URL}/api/v1/jobs/interviews/${id}/validate-token?token=${encodeURIComponent(urlToken)}`
      : `${API_BASE_URL}/api/v1/jobs/interviews/${id}/validate-token`

    const validateToken = async () => {
      try {
        const response = await fetch(apiUrl, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        })

        const data = await response.json()

        if (response.ok && data.valid) {
          const isVerifiedSession = Boolean(data.verified)
          const interviewMode: InterviewMode = data.interview?.interviewMode || 'ai'
          const requiresScreenSharing = data.interview?.requiresScreenSharing || false

          setTokenState({
            isValidating: false,
            isValid: true,
            error: null,
            interviewInfo: isVerifiedSession
              ? {
                  candidateName: data.interview?.candidateName || 'Candidate',
                  jobTitle: data.interview?.jobTitle || null,
                  duration: data.interview?.duration || null,
                }
              : null,
            interviewMode,
            requiresScreenSharing,
          })

          if (isVerifiedSession) {
            if (data.interview?.jobTitle) {
              setJobTitle(data.interview.jobTitle)
            }
            if (data.interview?.id) {
              setInterviewId(data.interview.id)
            }
            if (data.interview?.interviewMode === 'question_set') {
              const loadedQuestions = await loadQuestionSetOrFail(data.interview.id)
              if (!loadedQuestions) {
                return
              }
            }
            setSentryContext({
              interviewId: data.interview?.id || id,
              candidateName: data.interview?.candidateName || 'Candidate',
              jobTitle: data.interview?.jobTitle || null,
              sessionStatus: 'verified_session_restored',
            })
            setFlowState('consent')
          } else {
            setFlowState('otp')
          }
        } else {
          setTokenState({
            isValidating: false,
            isValid: false,
            error: data.error || 'Invalid or expired invitation token',
            interviewInfo: null,
            interviewMode: 'ai', // Reset to default
            requiresScreenSharing: false, // Reset to default
          })
          setFlowState('error')
        }
      } catch (error) {
        setTokenState({
          isValidating: false,
          isValid: false,
          error: 'Unable to validate invitation token. Please try again later.',
          interviewInfo: null,
          interviewMode: 'ai', // Reset to default
          requiresScreenSharing: false, // Reset to default
        })
        setFlowState('error')
      }
    }

    validateToken()
  }, [])

  useEffect(() => {
    if (resendAvailableAt <= Date.now()) {
      setResendCountdown(0)
      return
    }

    const updateCountdown = () => {
      const remainingSeconds = Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000))
      setResendCountdown(remainingSeconds)
    }

    updateCountdown()
    const intervalId = window.setInterval(updateCountdown, 1000)
    return () => window.clearInterval(intervalId)
  }, [resendAvailableAt])

  useEffect(() => {
    return () => {
      if (otpTransitionTimeoutRef.current) {
        window.clearTimeout(otpTransitionTimeoutRef.current)
      }
    }
  }, [])

  const canStart = agreedToTerms && agreedToRecording

  const failQuestionLoad = (message: string) => {
    setTokenState((current) => ({
      ...current,
      isValidating: false,
      isValid: false,
      error: message,
    }))
    setFlowState('error')
  }

  const loadQuestionSetOrFail = useCallback(
    async (nextInterviewId: string, nextToken?: string | null, proofToken?: string | null) => {
      try {
        const fetchedQuestions = await fetchInterviewQuestions(
          nextInterviewId,
          nextToken || undefined,
          proofToken || undefined
        )
        setQuestions(fetchedQuestions)
        return true
      } catch (error) {
        failQuestionLoad(
          error instanceof Error
            ? error.message
            : 'Unable to load interview questions. Please refresh the page and try again.'
        )
        return false
      }
    },
    []
  )

  const handleVerifyOtp = async () => {
    if (!publicInterviewId || !token || isOtpVerifiedTransitioning) return

    let otpTransitioning = false

    try {
      setIsVerifyingOtp(true)
      setOtpError(null)
      setOtpInfoMessage(null)
      const proofToken = await verifyInterviewOtp(publicInterviewId, token, otpCode)
      setOtpProofToken(proofToken)
      window.history.replaceState({}, document.title, `/${publicInterviewId}`)

      const accessDetails = await fetchInterviewAccessDetails(publicInterviewId, token, proofToken)
      setInterviewId(accessDetails.interviewId)
      setTokenState((current) => ({
        ...current,
        interviewInfo: {
          candidateName: accessDetails.candidateName,
          jobTitle: accessDetails.jobTitle,
          duration: accessDetails.duration,
        },
        interviewMode: accessDetails.interviewMode as InterviewMode,
        requiresScreenSharing: accessDetails.requiresScreenSharing,
      }))
      setJobTitle(accessDetails.jobTitle)

      setSentryContext({
        interviewId: accessDetails.interviewId,
        candidateName: accessDetails.candidateName,
        jobTitle: accessDetails.jobTitle,
        sessionStatus: 'otp_verified',
      })

      if (accessDetails.interviewMode === 'question_set') {
        const loadedQuestions = await loadQuestionSetOrFail(
          accessDetails.interviewId,
          token,
          proofToken
        )
        if (!loadedQuestions) {
          return
        }
      }

      otpTransitioning = true
      setIsOtpVerifiedTransitioning(true)
      setOtpInfoMessage('Access verified. Preparing your interview...')
      otpTransitionTimeoutRef.current = window.setTimeout(() => {
        setFlowState('consent')
      }, 950)
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 429 && error.retryAfterSeconds) {
        setOtpError(`${error.message} Try again in ${error.retryAfterSeconds}s.`)
      } else {
        setOtpError(error instanceof Error ? error.message : 'Failed to verify interview code')
      }
    } finally {
      if (!otpTransitioning) {
        setIsVerifyingOtp(false)
      }
    }
  }

  const handleResendOtp = async () => {
    if (!publicInterviewId || !token) return

    try {
      setIsResendingOtp(true)
      setOtpError(null)
      const result = await resendInterviewOtp(publicInterviewId, token)
      setOtpInfoMessage(
        result.expiresAt
          ? `A new code has been sent. It remains valid until your interview access expires.`
          : result.message
      )
      setResendAvailableAt(Date.now() + 30_000)
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 429 && error.retryAfterSeconds) {
        setResendAvailableAt(Date.now() + error.retryAfterSeconds * 1000)
        setOtpError(`Too many resend requests. Try again in ${error.retryAfterSeconds}s.`)
      } else {
        setOtpError(error instanceof Error ? error.message : 'Failed to resend interview code')
      }
    } finally {
      setIsResendingOtp(false)
    }
  }
  const handleStartInterview = () => {
    if (canStart) {
      // Go to device test screen first
      setFlowState('device_test')
    }
  }

  const handleDeviceConfirm = (config: DeviceConfig) => {
    setDeviceConfig(config)
    setFlowState('interview')
  }

  const handleInterviewEnd = (_recordedHistory?: ChatMessage[], _recordingData?: RecordingData) => {
    // Move to completed state
    setFlowState('completed')

    // Keep the thank-you screen visible, then redirect after 10 seconds
    setTimeout(() => {
      window.location.href = 'https://bo-smart.com/?utm_source=INTERVIEW_PLATFORM'
    }, 10000)
  }

  // Show loading state while validating token
  if (flowState === 'validating' || tokenState.isValidating) {
    return (
      <ErrorBoundary>
        <div className="w-full h-screen bg-dark-950 text-white font-sans flex flex-col items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 text-sm">Validating access...</p>
          </div>
        </div>
      </ErrorBoundary>
    )
  }

  // Show access denied screen for invalid tokens
  if (!tokenState.isValid) {
    return (
      <ErrorBoundary>
        <div className="flex min-h-screen w-full flex-col bg-[#eef3ed] text-[#1f2b28] font-sans">
          <header className="w-full bg-[#31453f]">
            <div className="mx-auto flex w-full max-w-[1440px] items-center justify-center px-6 py-5">
              <img
                src="/logo-new-light.png"
                alt="BO-SMART AI Logo"
                width="auto"
                height="48"
                className="h-12 object-contain"
              />
            </div>
            <div className="h-[6px] w-full bg-[#d9ef64]" />
          </header>

          <div className="relative flex flex-1 items-center justify-center overflow-hidden px-6 py-12">
            <div className="absolute inset-x-0 top-0 h-[42vh] bg-[#466d56] -z-10" />
            <div className="absolute inset-x-0 bottom-0 h-[58vh] bg-[#eef3ed] -z-10" />
            <div className="absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#d9ef64]/14 blur-[120px]" />

            <div className="relative z-10 mx-auto flex w-full max-w-[540px] flex-col items-center text-center">
              <img
                src="/404.svg"
                alt="Person illustration for unavailable page"
                className="h-auto w-full max-w-[380px] sm:max-w-[450px]"
              />
            </div>
          </div>

          <footer className="mt-auto border-t border-[#dfe4de] px-4 py-6 sm:px-6">
            <div className="mx-auto max-w-6xl text-sm text-[#5f675f]">
              <div className="flex flex-col items-center justify-between gap-3 text-center sm:flex-row sm:text-left">
                <p>
                  Need help? Contact{' '}
                  <a
                    href="mailto:recruitment@bo-smart.com"
                    className="text-[#24382d] hover:text-[#18211b] transition-colors"
                  >
                    recruitment@bo-smart.com
                  </a>
                </p>
                <a
                  href="https://www.bo-smart.com/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#5f675f] hover:text-[#18211b] transition-colors"
                >
                  Privacy Policy
                </a>
              </div>
              <p className="mt-4 text-center text-xs text-[#7c857c]">
                BO-SMART LTD {new Date().getFullYear()}
              </p>
            </div>
          </footer>
        </div>
      </ErrorBoundary>
    )
  }

  // Device Test Screen
  if (flowState === 'device_test') {
    return (
      <ErrorBoundary>
        <DeviceTestScreen
          onConfirm={handleDeviceConfirm}
          interviewInfo={tokenState.interviewInfo || undefined}
        />
      </ErrorBoundary>
    )
  }

  // Interview Session (AI or Sequential based on mode)
  if (flowState === 'interview' && interviewId && deviceConfig) {
    // Sequential mode for question_set interviews
    if (tokenState.interviewMode === 'question_set') {
      // Bug fix: Handle case where questions haven't loaded yet or failed to load
      if (questions.length === 0) {
        return (
          <ErrorBoundary>
            <div className="w-full h-screen bg-dark-950 text-white font-sans flex flex-col items-center justify-center">
              <div className="flex flex-col items-center gap-6 text-center px-6 max-w-md">
                <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-amber-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold">Unable to Load Questions</h1>
                <p className="text-gray-400">
                  The interview questions could not be loaded. Please refresh the page or contact HR
                  for assistance.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 px-6 py-3 bg-brand-600 hover:bg-brand-500 text-white font-semibold rounded-xl transition-colors"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </ErrorBoundary>
        )
      }

      return (
        <ErrorBoundary>
          <SequentialInterviewSession
            interviewId={interviewId}
            token={token}
            otpProofToken={otpProofToken || ''}
            questions={questions}
            deviceConfig={deviceConfig}
            onEnd={() => {
              setSentryContext({
                interviewId,
                candidateName: tokenState.interviewInfo?.candidateName,
                jobTitle: tokenState.interviewInfo?.jobTitle || null,
                sessionStatus: 'completed',
              })
              handleInterviewEnd()
            }}
            interviewInfo={tokenState.interviewInfo || undefined}
          />
        </ErrorBoundary>
      )
    }

    // AI conversational mode (default)
    return (
      <ErrorBoundary>
        <InterviewSession
          interviewId={interviewId}
          token={token}
          otpProofToken={otpProofToken || ''}
          onEnd={(history, recording) => {
            setSentryContext({
              interviewId,
              candidateName: tokenState.interviewInfo?.candidateName,
              jobTitle: tokenState.interviewInfo?.jobTitle || null,
              sessionStatus: 'completed',
            })
            handleInterviewEnd(history, recording)
          }}
        />
      </ErrorBoundary>
    )
  }

  // Completed state - Thank you screen
  if (flowState === 'completed') {
    return (
      <ErrorBoundary>
        <div className="w-full h-screen bg-dark-950 text-white font-sans flex flex-col items-center justify-center">
          <div className="flex flex-col items-center gap-6 text-center px-6">
            <div className="w-20 h-20 bg-brand-500/20 rounded-full flex items-center justify-center">
              <svg
                className="w-10 h-10 text-brand-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold">Thank You!</h1>
            <p className="text-gray-400 max-w-md">
              Your interview has been submitted successfully. HR will contact you regarding next
              steps in the recruitment process.
            </p>
            <p className="text-gray-500 text-sm">Redirecting in a few seconds...</p>
          </div>
        </div>
      </ErrorBoundary>
    )
  }

  // Consent Screen (default for valid tokens)
  return (
    <ErrorBoundary>
      <div className="w-full min-h-screen bg-[#eef3ed] text-[#18211b] font-sans selection:bg-brand-500/20 flex flex-col">
        <>
          {/* Header with Logo */}
          <header
            className="w-full border-b border-[#40584c] py-4 px-6 flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgb(43, 71, 66)' }}
          >
            <img
              src="/logo-new-light.png"
              alt="BO-SMART Logo"
              width="auto"
              height="44"
              className="h-10 object-contain"
            />
          </header>
          <div className="h-[10px] w-full bg-[#d2f34c] flex-shrink-0" />

          <div className="container mx-auto px-4 sm:px-6 flex-1 flex flex-col items-center justify-center py-8 sm:py-12 relative overflow-y-auto">
            {/* Background decoration */}
            <div className="absolute inset-x-0 top-0 h-[42vh] bg-[#466d56] -z-10 pointer-events-none" />
            <div className="absolute top-10 left-[8%] w-16 h-16 rounded-full bg-[#d2f34c]/10 -z-10 pointer-events-none" />
            <div className="absolute top-24 right-[7%] w-20 h-20 rounded-full bg-[#d2f34c]/12 -z-10 pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 h-[58vh] bg-[#eef3ed] -z-10 pointer-events-none" />

            <div
              className={`w-full rounded-[22px] border border-[#d8ddd7] bg-[#f7f8f4] shadow-[0_18px_48px_rgba(33,48,40,0.10)] animate-fade-in-up my-auto overflow-hidden ${flowState === 'otp' ? 'max-w-xl' : 'max-w-5xl'}`}
            >
              <div className={flowState === 'otp' ? '' : 'grid lg:grid-cols-[1.05fr_0.95fr]'}>
                {flowState !== 'otp' ? (
                  <section className="px-6 py-8 sm:px-8 sm:py-10 md:px-10 md:py-12 border-b border-[#d8ddd7] lg:border-b-0 lg:border-r lg:border-[#d8ddd7]">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#667065]">
                        Interview Invitation
                      </p>
                      <h1 className="mt-4 text-[2rem] sm:text-[2.5rem] leading-tight font-semibold tracking-tight text-[#18211b] truncate">
                        Welcome
                        {tokenState.interviewInfo?.candidateName
                          ? `, ${tokenState.interviewInfo.candidateName}`
                          : ''}
                      </h1>
                      <p className="mt-4 max-w-lg text-sm sm:text-[15px] leading-7 text-[#5f675f]">
                        Take a moment to review the interview details and confirm your consent
                        before you continue.
                      </p>
                    </div>

                    <div className="mt-10 space-y-6">
                      {jobTitle && (
                        <div className="pb-5 border-b border-[#d8ddd7]">
                          <p className="text-[11px] uppercase tracking-[0.2em] text-[#7c857c]">
                            Role
                          </p>
                          <p className="mt-2 text-lg font-medium text-[#18211b]">{jobTitle}</p>
                        </div>
                      )}

                      {tokenState.interviewInfo?.duration ? (
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.2em] text-[#7c857c]">
                            Estimated time
                          </p>
                          <p className="mt-2 text-lg font-medium text-[#18211b]">
                            {tokenState.interviewInfo.duration} mins
                          </p>
                          <p className="mt-2 text-sm leading-6 text-[#5f675f]">
                            Please allow at least this much time to complete the interview without
                            interruptions.
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </section>
                ) : null}

                <section className="px-6 py-8 sm:px-8 sm:py-10 md:px-10 md:py-12 bg-[#f3f4ef]">
                  <div className="max-w-md">
                    <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#7c857c]">
                      {flowState === 'otp' ? 'Verification' : 'Consent'}
                    </p>
                    <h2 className="mt-4 text-2xl font-semibold tracking-tight text-[#18211b]">
                      {flowState === 'otp' ? 'Enter your interview code' : 'Before you begin'}
                    </h2>
                    <p className="mt-3 text-sm leading-6 text-[#5f675f]">
                      {flowState === 'otp'
                        ? 'Use the 6-digit verification code from your interview invitation email to continue.'
                        : 'Both confirmations are required before the interview can start.'}
                    </p>
                  </div>

                  {flowState === 'otp' ? (
                    <div className="mt-8 mb-8">
                      {isOtpVerifiedTransitioning ? (
                        <div className="rounded-[24px] border border-[#d6e89b] bg-[#f6fbdc] px-6 py-8 text-center shadow-[0_12px_32px_rgba(171,205,58,0.16)] transition-all duration-500">
                          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#d2f34c] shadow-[0_0_0_10px_rgba(210,243,76,0.18)] animate-pulse">
                            <svg
                              className="h-8 w-8 text-[#244034]"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                          <h3 className="mt-5 text-xl font-semibold text-[#18211b]">
                            Verification successful
                          </h3>
                          <p className="mt-3 text-sm leading-6 text-[#375745]">
                            We&apos;re unlocking your interview and taking you to the next step.
                          </p>
                          <div className="mt-6 h-2 w-full overflow-hidden rounded-full bg-white/80">
                            <div
                              className="h-full w-full origin-left rounded-full bg-[#d2f34c]"
                              style={{ animation: 'growBar 0.9s ease-out forwards' }}
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <label className="block text-[11px] uppercase tracking-[0.2em] text-[#7c857c] mb-3">
                            Verification code
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            value={otpCode}
                            onChange={(e) =>
                              setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                            }
                            className="w-full rounded-xl border border-[#d2d8d0] bg-white px-4 py-3 text-lg tracking-[0.28em] text-[#18211b] outline-none focus:border-[#24382d]"
                            placeholder="000000"
                            disabled={isVerifyingOtp}
                          />
                          {otpError ? (
                            <p className="mt-3 text-sm text-[#a23b2a]">{otpError}</p>
                          ) : otpInfoMessage ? (
                            <p className="mt-3 text-sm text-[#375745]">{otpInfoMessage}</p>
                          ) : null}
                          <div className="mt-4 flex items-center justify-between gap-3 text-sm">
                            <span className="text-[#5f675f]">Did not receive the code?</span>
                            <button
                              type="button"
                              onClick={handleResendOtp}
                              disabled={isResendingOtp || resendCountdown > 0}
                              className={`font-medium underline underline-offset-4 transition-colors ${
                                isResendingOtp || resendCountdown > 0
                                  ? 'text-[#98a197] cursor-not-allowed no-underline'
                                  : 'text-[#24382d] hover:text-[#18211b]'
                              }`}
                            >
                              {isResendingOtp
                                ? 'Sending...'
                                : resendCountdown > 0
                                  ? `Resend in ${resendCountdown}s`
                                  : 'Resend code'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3 sm:space-y-4 mt-8 mb-8">
                      <label className="flex items-start gap-4 py-2 transition-colors cursor-pointer group">
                        <div className="relative flex items-center pt-0.5">
                          <input
                            type="checkbox"
                            className="peer sr-only"
                            checked={agreedToTerms}
                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                          />
                          <div
                            className={`w-5 h-5 border-2 rounded-md transition-all ${agreedToTerms ? 'bg-brand-600 border-brand-600' : 'border-[#9aa39a] group-hover:border-brand-500'}`}
                          ></div>
                          <svg
                            className={`absolute w-3.5 h-3.5 text-black left-1 top-1.5 pointer-events-none transition-opacity ${agreedToTerms ? 'opacity-100' : 'opacity-0'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-[#18211b] font-medium">Terms and privacy</p>
                          <p className="text-xs text-[#5f675f] mt-1 leading-5">
                            I have read the{' '}
                            <a
                              href="https://www.bo-smart.com/privacy-policy"
                              target="_blank"
                              rel="noreferrer"
                              className="text-[#24382d] underline underline-offset-2 hover:text-[#18211b]"
                            >
                              privacy policy
                            </a>{' '}
                            and agree to the interview terms and candidate consent requirements.
                          </p>
                        </div>
                      </label>

                      <label className="flex items-start gap-4 py-2 transition-colors cursor-pointer group">
                        <div className="relative flex items-center pt-0.5">
                          <input
                            type="checkbox"
                            className="peer sr-only"
                            checked={agreedToRecording}
                            onChange={(e) => setAgreedToRecording(e.target.checked)}
                          />
                          <div
                            className={`w-5 h-5 border-2 rounded-md transition-all ${agreedToRecording ? 'bg-brand-600 border-brand-600' : 'border-[#9aa39a] group-hover:border-brand-500'}`}
                          ></div>
                          <svg
                            className={`absolute w-3.5 h-3.5 text-black left-1 top-1.5 pointer-events-none transition-opacity ${agreedToRecording ? 'opacity-100' : 'opacity-0'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-[#18211b] font-medium">
                            Recording acknowledgement
                          </p>
                          <p className="text-xs text-[#5f675f] mt-1 leading-5">
                            {tokenState.requiresScreenSharing ? (
                              <>
                                I understand this session may include recording and screen sharing
                                for technical assessment, as outlined in the{' '}
                                <a
                                  href="https://www.bo-smart.com/privacy-policy"
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[#24382d] underline underline-offset-2 hover:text-[#18211b]"
                                >
                                  privacy policy
                                </a>
                                .
                              </>
                            ) : (
                              <>
                                I understand this session may be recorded as part of the assessment
                                process, as outlined in the{' '}
                                <a
                                  href="https://www.bo-smart.com/privacy-policy"
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[#24382d] underline underline-offset-2 hover:text-[#18211b]"
                                >
                                  privacy policy
                                </a>
                                .
                              </>
                            )}
                          </p>
                        </div>
                      </label>
                    </div>
                  )}

                  <button
                    onClick={flowState === 'otp' ? handleVerifyOtp : handleStartInterview}
                    disabled={
                      flowState === 'otp'
                        ? otpCode.length !== 6 || isVerifyingOtp || isOtpVerifiedTransitioning
                        : !canStart
                    }
                    className={`
                  w-full py-4 rounded-xl font-semibold text-sm uppercase tracking-[0.04em] transition-all duration-300 flex items-center justify-center
                  ${
                    (
                      flowState === 'otp'
                        ? otpCode.length === 6 && !isVerifyingOtp && !isOtpVerifiedTransitioning
                        : canStart
                    )
                      ? 'bg-[#d2f34c] text-[#244034] shadow-[0_4px_12px_rgba(210,243,76,0.3)] hover:bg-[#c6ea36] transform hover:-translate-y-0.5'
                      : 'bg-[#dfe4dc] text-[#8a9389] cursor-not-allowed border border-[#d2d8d0]'
                  }
                `}
                  >
                    <span>
                      {flowState === 'otp'
                        ? isOtpVerifiedTransitioning
                          ? 'Verified'
                          : isVerifyingOtp
                            ? 'Verifying...'
                            : 'Verify Code'
                        : 'Start Interview'}
                    </span>
                  </button>
                </section>
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="w-full py-6 sm:py-8 px-4 sm:px-6 flex-shrink-0">
            <div className="mx-auto flex w-full max-w-5xl flex-col items-center justify-between gap-4 text-center md:flex-row md:text-left">
              <div className="text-sm text-[#5f675f]">
                Need help? Contact{' '}
                <a
                  href="mailto:recruitment@bo-smart.com"
                  className="text-[#24382d] hover:text-[#18211b] transition-colors"
                >
                  recruitment@bo-smart.com
                </a>
              </div>
              <div className="flex items-center gap-5 text-sm">
                <a
                  href="https://www.bo-smart.com/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#5f675f] hover:text-[#18211b] transition-colors"
                >
                  Privacy Policy
                </a>
              </div>
            </div>
            <p className="mt-4 text-center text-xs text-[#7c857c]">
              BO-SMART LTD {new Date().getFullYear()}
            </p>
          </footer>
          <style>{`
          @keyframes growBar {
            from { transform: scaleX(0); }
            to { transform: scaleX(1); }
          }
        `}</style>
        </>
      </div>
    </ErrorBoundary>
  )
}
